/**
 * POS Product Button Color Coding by Category
 *
 * Colors are organized by product line for quick visual identification:
 * - 5 STAR Line = Reds (premium feed)
 * - PLUS Line = Blues
 * - ESS Line = Greens
 * - DOG products = Browns/Oranges
 * - CAT products = Purples
 * - BIRD products = Cyans
 */

export interface CategoryColor {
  bg: string;
  hover: string;
  text: string;
}

export const POS_COLORS: Record<string, CategoryColor> = {
  // 5 STAR LINE - RED FAMILY
  "5 STAR Bags": { bg: "#B91C1C", hover: "#991B1B", text: "#FFFFFF" },
  "5 STAR Ton": { bg: "#DC2626", hover: "#B91C1C", text: "#FFFFFF" },
  "5 STAR 1000": { bg: "#EF4444", hover: "#DC2626", text: "#FFFFFF" },
  "BYOC 5 STAR": { bg: "#7F1D1D", hover: "#991B1B", text: "#FFFFFF" },

  // PLUS LINE - BLUE FAMILY
  "PLUS Ton": { bg: "#1D4ED8", hover: "#1E40AF", text: "#FFFFFF" },
  "PLUS 1000": { bg: "#2563EB", hover: "#1D4ED8", text: "#FFFFFF" },
  "PLUS bags": { bg: "#3B82F6", hover: "#2563EB", text: "#FFFFFF" },

  // ESS LINE - GREEN FAMILY
  "ESS Ton": { bg: "#15803D", hover: "#166534", text: "#FFFFFF" },
  "ESS 1000": { bg: "#16A34A", hover: "#15803D", text: "#FFFFFF" },
  "ESS Bags": { bg: "#22C55E", hover: "#16A34A", text: "#FFFFFF" },

  // PET PRODUCTS - DOGS (Browns/Oranges)
  "DOG": { bg: "#92400E", hover: "#78350F", text: "#FFFFFF" },
  "DOG TREATS": { bg: "#EA580C", hover: "#C2410C", text: "#FFFFFF" },
  "Canned Dog Food": { bg: "#78350F", hover: "#451A03", text: "#FFFFFF" },

  // PET PRODUCTS - CATS (Purples)
  "CAT": { bg: "#7C3AED", hover: "#6D28D9", text: "#FFFFFF" },
  "Canned Cat Food": { bg: "#5B21B6", hover: "#4C1D95", text: "#FFFFFF" },
  "CAT LITTER": { bg: "#6B7280", hover: "#4B5563", text: "#FFFFFF" },

  // PET PRODUCTS - BIRDS (Cyans)
  "WILD BIRD": { bg: "#0891B2", hover: "#0E7490", text: "#FFFFFF" },
  "Bird Bars": { bg: "#06B6D4", hover: "#0891B2", text: "#FFFFFF" },

  // SPECIALTY BRANDS
  "Redmond": { bg: "#0D9488", hover: "#0F766E", text: "#FFFFFF" },
  "Waterloo Ranch": { bg: "#881337", hover: "#4C0519", text: "#FFFFFF" },
  "Red Bridge Laser Forge": { bg: "#BE123C", hover: "#9F1239", text: "#FFFFFF" },
  "Steves Woodcrafts": { bg: "#A16207", hover: "#854D0E", text: "#FFFFFF" },
  "Blocks": { bg: "#374151", hover: "#1F2937", text: "#FFFFFF" },

  // INGREDIENTS & SUPPLIES
  "Ingredient Bags": { bg: "#65A30D", hover: "#4D7C0F", text: "#FFFFFF" },
  "Mill Ingredients": { bg: "#4D7C0F", hover: "#3F6212", text: "#FFFFFF" },
  "Whole Grains": { bg: "#D97706", hover: "#B45309", text: "#FFFFFF" },
  "Shipment Packaging": { bg: "#9CA3AF", hover: "#6B7280", text: "#1F2937" },
  "Retail Bags": { bg: "#D1D5DB", hover: "#9CA3AF", text: "#1F2937" },

  // STORE & OTHER
  "Store": { bg: "#FBBF24", hover: "#F59E0B", text: "#1F2937" },
  "Wood": { bg: "#A3A3A3", hover: "#737373", text: "#1F2937" },
  "DIRT": { bg: "#78716C", hover: "#57534E", text: "#FFFFFF" },
};

export const DEFAULT_COLOR: CategoryColor = {
  bg: "#6B7280",
  hover: "#4B5563",
  text: "#FFFFFF"
};

/**
 * Get color scheme for a category name.
 * Handles whitespace trimming and returns default color if no match.
 */
export const getCategoryColor = (category: string | null | undefined): CategoryColor => {
  if (!category) return DEFAULT_COLOR;

  const normalized = category.trim();

  // Direct match
  if (POS_COLORS[normalized]) {
    return POS_COLORS[normalized];
  }

  // Try case-insensitive match
  const lowerCategory = normalized.toLowerCase();
  for (const [key, value] of Object.entries(POS_COLORS)) {
    if (key.toLowerCase() === lowerCategory) {
      return value;
    }
  }

  return DEFAULT_COLOR;
};
